/**
 * OpenTelemetry instrumentation setup.
 *
 * Opt-in: Only activates when OTEL_ENABLED=true.
 * When disabled, zero overhead (no OTel packages loaded).
 *
 * Loaded by main.ts BEFORE Knex to ensure proper ESM patching.
 *
 * HTTP tracing comes from the `@hono/otel` middleware mounted in
 * `src/http/app.ts` — no Express instrumentation here.
 *
 * Instruments: HTTP (server), Knex (DB), Pino (log correlation), Node.js
 * runtime (event loop, GC, heap). Exports: Prometheus metrics on
 * OTEL_PROMETHEUS_PORT (default 9464). Optional OTLP trace export.
 */

import { getOtelConfig } from './modules/observability/observability.config.js'

type NodeSDKInstance = { start: () => void; shutdown: () => Promise<void> }
let sdk: NodeSDKInstance | null = null

// ── Auto-init at import time (top-level await) ─────────────────────────────
// main.ts imports this module BEFORE Knex, so OTel hooks are in place when
// those libraries load.

const config = getOtelConfig()

if (config.enabled) {
  const [
    { NodeSDK },
    { HttpInstrumentation },
    { KnexInstrumentation },
    { PinoInstrumentation },
    { PrometheusExporter },
    { RuntimeNodeInstrumentation }
  ] = await Promise.all([
    import('@opentelemetry/sdk-node'),
    import('@opentelemetry/instrumentation-http'),
    import('@opentelemetry/instrumentation-knex'),
    import('@opentelemetry/instrumentation-pino'),
    import('@opentelemetry/exporter-prometheus'),
    import('@opentelemetry/instrumentation-runtime-node')
  ])

  const sdkOptions: ConstructorParameters<typeof NodeSDK>[0] = {
    serviceName: config.serviceName,
    metricReader: new PrometheusExporter({ port: config.prometheusPort }),
    instrumentations: [
      new HttpInstrumentation(),
      new KnexInstrumentation(),
      new PinoInstrumentation(),
      new RuntimeNodeInstrumentation()
    ]
  }

  if (config.otlpEndpoint) {
    const { OTLPTraceExporter } = await import('@opentelemetry/exporter-trace-otlp-http')
    sdkOptions.traceExporter = new OTLPTraceExporter({
      url: config.otlpEndpoint
    })
  }

  if (config.traceSampleRate < 1) {
    process.env['OTEL_TRACES_SAMPLER'] = 'traceidratio'
    process.env['OTEL_TRACES_SAMPLER_ARG'] = String(config.traceSampleRate)
  }

  sdk = new NodeSDK(sdkOptions)
  sdk.start()
  console.log(`[otel] Instrumentation active — metrics on :${config.prometheusPort}/metrics`)
}

/**
 * No-op: init happens at import time via top-level await.
 * Kept for server.ts lifecycle compatibility.
 */
export async function initTelemetry(): Promise<void> {}

/**
 * Gracefully shutdown the OTel SDK, flushing pending telemetry.
 * No-op if OTel was not initialized.
 */
export async function shutdownTelemetry(): Promise<void> {
  if (!sdk) return
  await sdk.shutdown()
  sdk = null
}
